import { Fragment, type ReactNode, useState } from "react";

type RichBlock =
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "quote"; text: string }
  | { type: "image"; alt: string; url: string; caption?: string }
  | { type: "divider" };

interface BlogRichContentProps {
  body?: string | null;
  className?: string;
  emptyMessage?: string;
}

interface ParsedImageLine {
  alt: string;
  url: string;
  caption?: string;
}

function normalizeInlineWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function normalizeBlockWhitespace(value: string): string {
  return value.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

function cleanMarkdownToken(value: string): string {
  return value.replace(/[\[\]\(\)]/g, "").replace(/"/g, "'").trim();
}

function joinSerializedSegments(segments: string[]): string {
  return segments.reduce((accumulator, current) => {
    if (!current) {
      return accumulator;
    }
    if (!accumulator) {
      return current;
    }
    const needsSpace = /[A-Za-z0-9)\]]$/.test(accumulator) && /^[A-Za-z0-9(\[]/.test(current);
    return `${accumulator}${needsSpace ? " " : ""}${current}`;
  }, "");
}

function normalizeBodyForRichParsing(body: string): string {
  let normalized = body.replace(/\r\n/g, "\n").replace(/\u00a0/g, " ");

  const escapedBreakCount = (normalized.match(/\\n/g) || []).length;
  if (escapedBreakCount >= 2) {
    normalized = normalized.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
  }

  if (hasLikelyHtmlMarkup(normalized)) {
    normalized = htmlToRichMarkdown(normalized);
  }

  // Isolate common markdown block tokens that may arrive inline after paste.
  normalized = normalized.replace(/(!\[[^\]]*\]\([^)]+\))/g, "\n\n$1\n\n");
  normalized = normalized.replace(/([^\n])\s*(#{1,3})(?=\s*[^\s#])/g, "$1\n\n$2 ");
  normalized = normalized.replace(/([^\n])\s*(>\s*)/g, "$1\n\n$2");
  normalized = normalized.replace(/([^\n])\s*(---+)\s*/g, "$1\n\n$2\n\n");
  normalized = normalized.replace(/([^\n])\s*(\d+\.(?:\s+|(?=[A-Za-z])))/g, "$1\n$2");
  normalized = normalized.replace(/([^\n])\s*([*-](?:\s+|(?=[A-Za-z])))/g, "$1\n$2");
  normalized = normalized.replace(/(\d+\.\s+.*?)(?=\s+\d+\.\s+)/g, "$1\n");
  normalized = normalized.replace(/([*-]\s+.*?)(?=\s+[*-]\s+)/g, "$1\n");

  // Recover missing spaces after punctuation and around quote marks.
  normalized = normalized.replace(/([a-zA-Z])(\d+\.)/g, "$1 $2");
  normalized = normalized.replace(/(\d+\.)([A-Za-z])/g, "$1 $2");
  normalized = normalized.replace(/([.!?;:,"”])([A-Z])/g, "$1 $2");

  // Recover merged words from some plain-text exports (e.g., "PerspectiveBy").
  normalized = normalized.replace(/([a-z]{3,})([A-Z][a-z])/g, "$1 $2");

  // Recover list bullets that often arrive as inline plain-text separators.
  normalized = normalized.replace(/^[•●▪◦‣]\s*/gm, "- ");
  normalized = normalized.replace(/([^\n])\s*[•●▪◦‣]\s*/g, "$1\n- ");

  // Recover heading + sentence lines collapsed by external sanitizers.
  normalized = normalized.replace(
    /^((?:[A-Z][A-Za-z'/-]*(?:\s+[A-Z][A-Za-z'/-]*){1,10}))\s+([A-Z][a-z]+\s+[a-z][^\n]*)$/gm,
    (_match: string, heading: string, remainder: string) => `## ${heading.trim()}\n\n${remainder.trim()}`,
  );

  return normalized.replace(/\n{3,}/g, "\n\n").trim();
}

function splitLongParagraph(text: string): string[] {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) {
    return [];
  }
  if (clean.length < 220) {
    return [clean];
  }

  const sentences = (clean.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [])
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (sentences.length < 3) {
    return [clean];
  }

  const chunks: string[] = [];
  let bucket: string[] = [];
  for (const sentence of sentences) {
    bucket.push(sentence);
    if (bucket.length >= 2) {
      chunks.push(bucket.join(" "));
      bucket = [];
    }
  }
  if (bucket.length > 0) {
    chunks.push(bucket.join(" "));
  }
  return chunks;
}

function toSafeUrl(source: string): string | null {
  const candidate = source.trim();
  if (!candidate) {
    return null;
  }
  if (candidate.startsWith("/")) {
    return candidate;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return candidate;
    }
    return null;
  } catch {
    return null;
  }
}

function hasLikelyHtmlMarkup(value: string): boolean {
  return /<([a-z][\w-]*)(\s[^>]*)?>/i.test(value) && /<\/?[a-z]/i.test(value);
}

function htmlToRichMarkdown(source: string): string {
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(source, "text/html");

  function childrenAsText(node: HTMLElement): string {
    return joinSerializedSegments(Array.from(node.childNodes).map(serializeNode));
  }

  function serializeNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\u00a0/g, " ");
    }

    if (!(node instanceof HTMLElement)) {
      return "";
    }

    const tag = node.tagName.toLowerCase();

    if (tag === "script" || tag === "style") {
      return "";
    }

    if (tag === "br") {
      return "\n";
    }

    if (tag === "strong" || tag === "b") {
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `**${content}**` : "";
    }

    if (tag === "em" || tag === "i") {
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `*${content}*` : "";
    }

    if (tag === "code" && node.parentElement?.tagName.toLowerCase() !== "pre") {
      const content = normalizeInlineWhitespace(node.textContent || "");
      return content ? `\`${content}\`` : "";
    }

    if (tag === "pre") {
      const content = (node.textContent || "").trim();
      return content ? `\n\n\`\`\`\n${content}\n\`\`\`\n\n` : "";
    }

    if (tag === "a") {
      const label = normalizeInlineWhitespace(childrenAsText(node)) || "Link";
      const href = toSafeUrl(node.getAttribute("href") || "");
      return href ? `[${label}](${href})` : label;
    }

    if (tag === "img") {
      const src = toSafeUrl(node.getAttribute("src") || "");
      if (!src) {
        return "";
      }
      const alt = cleanMarkdownToken(node.getAttribute("alt") || "") || "Embedded image";
      const caption = cleanMarkdownToken(node.getAttribute("title") || "");
      const captionToken = caption ? ` "${caption}"` : "";
      return `\n\n![${alt}](${src}${captionToken})\n\n`;
    }

    if (tag === "h1" || tag === "h2" || tag === "h3") {
      const level = tag === "h1" ? "#" : tag === "h2" ? "##" : "###";
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `\n\n${level} ${content}\n\n` : "";
    }

    if (tag === "blockquote") {
      const content = normalizeBlockWhitespace(childrenAsText(node));
      if (!content) {
        return "";
      }
      const lines = content
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => `> ${line}`)
        .join("\n");
      return lines ? `\n\n${lines}\n\n` : "";
    }

    if (tag === "ul" || tag === "ol") {
      const ordered = tag === "ol";
      const items = Array.from(node.children)
        .filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName.toLowerCase() === "li")
        .map((item, index) => {
          const content = normalizeBlockWhitespace(childrenAsText(item)).replace(/\s*\n\s*/g, " ");
          if (!content) {
            return "";
          }
          return ordered ? `${index + 1}. ${content}` : `- ${content}`;
        })
        .filter(Boolean)
        .join("\n");
      return items ? `\n\n${items}\n\n` : "";
    }

    if (
      tag === "p" ||
      tag === "div" ||
      tag === "section" ||
      tag === "article" ||
      tag === "header" ||
      tag === "footer" ||
      tag === "main" ||
      tag === "aside" ||
      tag === "figure" ||
      tag === "figcaption" ||
      tag === "li"
    ) {
      const content = normalizeBlockWhitespace(childrenAsText(node));
      return content ? `\n\n${content}\n\n` : "";
    }

    return childrenAsText(node);
  }

  const markdown = Array.from(documentNode.body.childNodes)
    .map(serializeNode)
    .join("");

  return normalizeBlockWhitespace(markdown);
}

function parseImageLine(line: string): ParsedImageLine | null {
  const match = line.match(/^!\[([^\]]*)\]\(\s*(<[^>]+>|[^)\s]+)(?:\s+["']([^"']+)["'])?\s*\)$/);
  if (!match) {
    return null;
  }
  const rawUrl = (match[2] || "").replace(/^<|>$/g, "");
  const safeUrl = toSafeUrl(rawUrl);
  if (!safeUrl) {
    return null;
  }
  return {
    alt: (match[1] || "").trim(),
    url: safeUrl,
    caption: (match[3] || "").trim() || undefined,
  };
}

function parseBlocks(body: string): RichBlock[] {
  const lines = normalizeBodyForRichParsing(body).split("\n");
  const blocks: RichBlock[] = [];
  let paragraphBuffer: string[] = [];
  let listBuffer: string[] = [];
  let listOrdered = false;
  let quoteBuffer: string[] = [];

  function shouldPreserveExplicitLineBreaks(paragraphLines: string[]): boolean {
    if (paragraphLines.length <= 1) {
      return false;
    }

    const terminalPunctuation = /[.!?:;"')\]]$/;
    let continuationPairCount = 0;

    for (let index = 0; index < paragraphLines.length - 1; index += 1) {
      const current = paragraphLines[index].trim();
      const next = paragraphLines[index + 1].trim();
      if (!current || !next) {
        continue;
      }
      const nextStartsLikeContinuation = /^[a-z(]/.test(next);
      if (!terminalPunctuation.test(current) && nextStartsLikeContinuation) {
        continuationPairCount += 1;
      }
    }

    return continuationPairCount < Math.max(1, Math.floor((paragraphLines.length - 1) * 0.6));
  }

  function flushParagraph(): void {
    if (paragraphBuffer.length === 0) {
      return;
    }
    const trimmedLines = paragraphBuffer.map((line) => line.trim()).filter(Boolean);
    if (trimmedLines.length === 0) {
      paragraphBuffer = [];
      return;
    }

    const segments = shouldPreserveExplicitLineBreaks(trimmedLines)
      ? trimmedLines.flatMap((line) => splitLongParagraph(line))
      : splitLongParagraph(trimmedLines.join(" "));

    for (const segment of segments) {
      blocks.push({ type: "paragraph", text: segment });
    }
    paragraphBuffer = [];
  }

  function flushList(): void {
    if (listBuffer.length === 0) {
      return;
    }
    blocks.push({ type: "list", ordered: listOrdered, items: listBuffer.map((entry) => entry.trim()).filter(Boolean) });
    listBuffer = [];
  }

  function flushQuote(): void {
    if (quoteBuffer.length === 0) {
      return;
    }
    blocks.push({ type: "quote", text: quoteBuffer.join(" ").trim() });
    quoteBuffer = [];
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      flushQuote();
      continue;
    }

    const image = parseImageLine(line);
    if (image) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({
        type: "image",
        alt: image.alt,
        url: image.url,
        caption: image.caption,
      });
      continue;
    }

    if (/^---+$/.test(line)) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({ type: "divider" });
      continue;
    }

    const heading = line.match(/^(#{1,3})\s*(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({
        type: "heading",
        level: heading[1].length as 1 | 2 | 3,
        text: heading[2].trim(),
      });
      continue;
    }

    const quote = line.match(/^>\s?(.*)$/);
    if (quote) {
      flushParagraph();
      flushList();
      quoteBuffer.push((quote[1] || "").trim());
      continue;
    }

    const ordered = line.match(/^\d+\.\s*(.+)$/);
    if (ordered) {
      flushParagraph();
      flushQuote();
      if (listBuffer.length > 0 && !listOrdered) {
        flushList();
      }
      listOrdered = true;
      listBuffer.push(ordered[1]);
      continue;
    }

    const unordered = line.match(/^[-*]\s*(.+)$/);
    if (unordered) {
      flushParagraph();
      flushQuote();
      if (listBuffer.length > 0 && listOrdered) {
        flushList();
      }
      listOrdered = false;
      listBuffer.push(unordered[1]);
      continue;
    }

    flushList();
    flushQuote();
    paragraphBuffer.push(line);
  }

  flushParagraph();
  flushList();
  flushQuote();

  return blocks;
}

function renderInline(content: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const tokenPattern = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\(([^)\s]+)\))/;
  let remaining = content;
  let tokenIndex = 0;

  while (remaining.length > 0) {
    const match = remaining.match(tokenPattern);
    if (!match || typeof match.index !== "number") {
      nodes.push(<Fragment key={`${keyPrefix}-text-${tokenIndex}`}>{remaining}</Fragment>);
      break;
    }

    const leading = remaining.slice(0, match.index);
    if (leading) {
      nodes.push(<Fragment key={`${keyPrefix}-text-${tokenIndex}`}>{leading}</Fragment>);
      tokenIndex += 1;
    }

    const token = match[0];
    if (token.startsWith("`")) {
      nodes.push(
        <code
          key={`${keyPrefix}-code-${tokenIndex}`}
          className="rounded bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)] px-1.5 py-0.5 text-[0.92em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("**")) {
      nodes.push(
        <strong key={`${keyPrefix}-strong-${tokenIndex}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("*")) {
      nodes.push(
        <em key={`${keyPrefix}-em-${tokenIndex}`} className="italic">
          {token.slice(1, -1)}
        </em>,
      );
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      const label = link?.[1] || token;
      const href = link?.[2] ? toSafeUrl(link[2]) : null;
      if (href) {
        nodes.push(
          <a
            key={`${keyPrefix}-link-${tokenIndex}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[var(--pr-accent-deep)] underline decoration-[color-mix(in_srgb,var(--pr-accent)_60%,transparent)] underline-offset-4"
          >
            {label}
          </a>,
        );
      } else {
        nodes.push(<Fragment key={`${keyPrefix}-link-fallback-${tokenIndex}`}>{label}</Fragment>);
      }
    }

    remaining = remaining.slice(match.index + token.length);
    tokenIndex += 1;
  }

  return nodes;
}

function RichImageFigure({ url, alt, caption }: { url: string; alt: string; caption?: string }) {
  const [isBroken, setIsBroken] = useState(false);

  if (isBroken) {
    return (
      <figure className="overflow-hidden rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
        <div className="grid min-h-40 place-items-center px-4 py-5 text-center text-sm text-[var(--pr-text-muted)]">
          <div className="space-y-2">
            <p>Image unavailable.</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[var(--pr-accent-deep)] underline underline-offset-4"
            >
              Open image
            </a>
          </div>
        </div>
        {caption ? <figcaption className="border-t border-[var(--pr-border)] px-4 py-2 text-sm text-[var(--pr-text-muted)]">{caption}</figcaption> : null}
      </figure>
    );
  }

  return (
    <figure className="overflow-hidden rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
      <img
        src={url}
        alt={alt || "Embedded blog media"}
        loading="lazy"
        className="h-auto max-h-[520px] w-full object-cover"
        onError={() => setIsBroken(true)}
      />
      {caption ? <figcaption className="border-t border-[var(--pr-border)] px-4 py-2 text-sm text-[var(--pr-text-muted)]">{caption}</figcaption> : null}
    </figure>
  );
}

export function BlogRichContent({
  body,
  className = "",
  emptyMessage = "This post does not include long-form content yet.",
}: BlogRichContentProps) {
  const source = (body || "").trim();
  if (!source) {
    return <p className="text-sm text-[var(--pr-text-muted)]">{emptyMessage}</p>;
  }

  const blocks = parseBlocks(source);

  return (
    <article className={`space-y-4 break-words text-[16px] leading-7 text-[var(--pr-text)] sm:space-y-5 sm:text-base ${className}`.trim()}>
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          if (block.level === 1) {
            return (
              <h2 key={`rich-heading-${index}`} className="pr-display text-[1.7rem] font-semibold leading-tight sm:text-3xl">
                {renderInline(block.text, `rich-heading-${index}`)}
              </h2>
            );
          }
          if (block.level === 2) {
            return (
              <h3 key={`rich-heading-${index}`} className="pr-display text-[1.4rem] font-semibold leading-tight sm:text-2xl">
                {renderInline(block.text, `rich-heading-${index}`)}
              </h3>
            );
          }
          return (
            <h4 key={`rich-heading-${index}`} className="text-lg font-semibold leading-tight">
              {renderInline(block.text, `rich-heading-${index}`)}
            </h4>
          );
        }

        if (block.type === "paragraph") {
          return (
            <p key={`rich-paragraph-${index}`} className="text-[16px] leading-7 sm:text-base">
              {renderInline(block.text, `rich-paragraph-${index}`)}
            </p>
          );
        }

        if (block.type === "divider") {
          return (
            <div key={`rich-divider-${index}`} className="py-1">
              <hr className="border-0 border-t border-[var(--pr-border)]" />
            </div>
          );
        }

        if (block.type === "quote") {
          return (
            <blockquote
              key={`rich-quote-${index}`}
              className="rounded-2xl border-l-4 border-[color-mix(in_srgb,var(--pr-accent)_75%,transparent)] bg-[color-mix(in_srgb,var(--pr-surface-secondary)_90%,transparent)] px-4 py-3 text-[16px] italic sm:text-base"
            >
              {renderInline(block.text, `rich-quote-${index}`)}
            </blockquote>
          );
        }

        if (block.type === "list") {
          const ListTag = block.ordered ? "ol" : "ul";
          return (
            <ListTag
              key={`rich-list-${index}`}
              className={`space-y-2 pl-5 text-[16px] leading-7 sm:text-base ${block.ordered ? "list-decimal" : "list-disc"}`}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`rich-list-item-${index}-${itemIndex}`}>{renderInline(item, `rich-list-item-${index}-${itemIndex}`)}</li>
              ))}
            </ListTag>
          );
        }

        return <RichImageFigure key={`rich-image-${index}`} url={block.url} alt={block.alt || "Embedded blog media"} caption={block.caption} />;
      })}
    </article>
  );
}
